import { HumanizeDurationOptions } from "humanize-duration-ts";

import { DateTime } from "@bps/date-time";
import {
  FontIcon,
  Stack,
  Text,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize,
  useTheme
} from "@bps/fluent-ui";
import { FieldDeviceProperty } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import humanizer from "@libs/common/humanize-duration";

interface ConnectivityBadgeProps {
  property?: FieldDeviceProperty;
}

export const ConnectivityBadge = ({ property }: ConnectivityBadgeProps) => {
  const theme = useTheme();
  if (property?.lastObservedUtc == undefined)
    return (
      <TextBadge
        badgeColor={TextBadgeColor.grey}
        badgeSize={TextBadgeSize.small}
      >
        Connectivity not reported
      </TextBadge>
    );

  const nowUtc = DateTime.now().setZone("utc");

  const statusBadgeHumanizeOptions = {
    units: ["d", "h"],
    round: true
  } as HumanizeDurationOptions;

  const statusAgeHumanizedLabel = humanizer.humanize(
    property.lastObservedUtc.diff(nowUtc).milliseconds,
    statusBadgeHumanizeOptions
  );

  return (
    <Stack
      horizontal
      tokens={{ childrenGap: 30 }}
      styles={{ root: { display: "flex", alignItems: "center" } }}
    >
      {property.propertyValue === "True" ? (
        <FontIcon
          iconName="CompletedSolid"
          styles={{
            root: {
              color: theme.palette.green,
              fontSize: 16
            }
          }}
        />
      ) : (
        <FontIcon
          iconName="StatusErrorFull"
          styles={{
            root: {
              color: theme.palette.red,
              fontSize: 16
            }
          }}
        />
      )}

      <Stack
        horizontal
        tokens={{ childrenGap: 10 }}
        styles={{ root: { display: "flex", alignItems: "center" } }}
      >
        <TextBadge
          badgeColor={getObservedBadgeColor(property.lastObservedUtc)}
          badgeSize={TextBadgeSize.small}
        >
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 5 }}>
            <FontIcon iconName="Globe" />
            <Stack.Item>{statusAgeHumanizedLabel} ago</Stack.Item>
            <Stack
              styles={{
                root: {
                  alignContent: "end",
                  alignItems: "end",
                  flexWrap: "wrap"
                }
              }}
            />
          </Stack>
        </TextBadge>
        <Text variant="xSmall">
          {property.lastObservedUtc.toFormat("FFFF")}
        </Text>
      </Stack>
    </Stack>
  );
};

function getObservedBadgeColor(lastObservedUtc: DateTime): TextBadgeColor {
  const nowUtc = DateTime.now().setZone("utc");
  const age = nowUtc.diff(lastObservedUtc, "days").days;

  if (age <= 1) return TextBadgeColor.green; // Green if last observed within a day
  if (age <= 2) return TextBadgeColor.yellow; // Yellow if last observed within two days but more then 1 day.
  return TextBadgeColor.red; // Red if longer then 2 days ago
}
